import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Phone, Search, Plus, Trash2, Loader2, PhoneIncoming, Tag, Shield, Check, ChevronDown, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CHANNELS = [
  'Google Business Profile', 'Website', 'Google Ads', 'Facebook Ads', 'Instagram',
  'Billboard', 'Flyer / Print', 'Direct Mail', 'Referral',
  'Radio / TV', 'Door Hanger', 'Business Card', 'Other',
];

const CHANNEL_SEO_INFO = {
  'Website': 'Uses DNI script — SEO safe.',
  'Google Business Profile': 'Set as GBP Primary phone.',
  'Google Ads': 'No SEO impact.',
  'Facebook Ads': 'No SEO impact.',
};

function formatPhone(val) {
  const digits = (val || '').replace(/\D/g, '');
  const d = digits.startsWith('1') && digits.length > 10 ? digits.slice(1) : digits;
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0,3)}) ${d.slice(3)}`;
  return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6,10)}`;
}

function toE164(val) {
  const digits = (val || '').replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits[0] === '1') return `+${digits}`;
  return val;
}

function PhoneInput({ value, onChange, placeholder, className, ...props }) {
  const handleChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    const trimmed = raw.startsWith('1') && raw.length > 10 ? raw.slice(1) : raw;
    onChange(trimmed.slice(0, 10));
  };
  return (
    <Input value={formatPhone(value || '')} onChange={handleChange}
      placeholder={placeholder || '(817) 555-1234'} className={className} type="tel" {...props} />
  );
}

export default function AdminCallTracking({ getHeaders }) {
  const [overview, setOverview] = useState({ clients: [], total_numbers: 0 });
  const [clients, setClients] = useState([]);
  const [ownedNumbers, setOwnedNumbers] = useState([]);
  const [loadingOwned, setLoadingOwned] = useState(false);
  const [loading, setLoading] = useState(true);

  // Assign state
  const [assignSlug, setAssignSlug] = useState('');
  const [selectedNumber, setSelectedNumber] = useState('');
  const [assignForward, setAssignForward] = useState('');
  const [assignChannel, setAssignChannel] = useState('Google Business Profile');
  const [assigning, setAssigning] = useState(false);
  const [msg, setMsg] = useState('');

  // Buy new state
  const [showBuyNew, setShowBuyNew] = useState(false);
  const [areaCode, setAreaCode] = useState('817');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [buying, setBuying] = useState(false);

  // Auto-fill forward number when client is selected
  const handleClientSelect = (slug) => {
    setAssignSlug(slug);
    if (slug) {
      const client = clients.find(c => c.slug === slug);
      if (client) {
        // Pull phone from client data (dashboard_clients)
        const phone = client.phone || client.alt_phone || '';
        const digits = phone.replace(/\D/g, '');
        const clean = digits.startsWith('1') && digits.length > 10 ? digits.slice(1) : digits;
        setAssignForward(clean);
      }
    } else {
      setAssignForward('');
    }
  };

  const hdrs = () => ({ ...getHeaders(), 'Content-Type': 'application/json' });

  const fetchData = () => {
    Promise.all([
      fetch(`${API}/api/admin/call-tracking/overview`, { headers: getHeaders() }).then(r => r.json()),
      fetch(`${API}/api/admin/dashboard-clients`, { headers: getHeaders() }).then(r => r.json()),
    ]).then(([ov, cl]) => {
      setOverview(ov);
      setClients(Array.isArray(cl) ? cl : cl.clients || []);
    }).catch(console.error).finally(() => setLoading(false));
  };

  const fetchOwned = async () => {
    setLoadingOwned(true);
    try {
      const r = await fetch(`${API}/api/admin/call-tracking/owned-numbers`, { headers: getHeaders() });
      const d = await r.json();
      setOwnedNumbers(d.numbers || []);
    } catch (e) { console.error(e); }
    setLoadingOwned(false);
  };

  useEffect(() => { fetchData(); fetchOwned(); }, [getHeaders]);

  const assignOwned = async () => {
    if (!assignSlug) return setMsg('Select a client first');
    if (!selectedNumber) return setMsg('Select a number from your inventory');
    if (!assignForward) return setMsg('Enter the client\'s real business phone number');
    setAssigning(true); setMsg('');
    try {
      const res = await fetch(`${API}/api/admin/call-tracking/assign/${assignSlug}`, {
        method: 'POST', headers: hdrs(),
        body: JSON.stringify({ phone_number: selectedNumber, forward_to: toE164(assignForward), channel: assignChannel }),
      });
      const d = await res.json();
      if (res.ok && d.success) {
        setMsg(`Assigned ${d.tracking_number} to ${assignSlug} [${d.channel}]`);
        setSelectedNumber('');
        fetchData(); fetchOwned();
      } else {
        setMsg(d.detail || 'Failed to assign number');
      }
    } catch (e) { setMsg('Error: ' + e.message); }
    setAssigning(false);
  };

  const searchNumbers = async () => {
    if (!areaCode) return;
    setSearching(true); setSearchResults([]);
    try {
      const res = await fetch(`${API}/api/admin/call-tracking/available-numbers?area_code=${areaCode}`, { headers: getHeaders() });
      const d = await res.json();
      setSearchResults(d.numbers || []);
    } catch (e) { console.error(e); }
    setSearching(false);
  };

  const buyNumber = async (phone) => {
    setBuying(true);
    try {
      // Use provision endpoint to buy — but we don't assign yet
      const res = await fetch(`${API}/api/admin/call-tracking/provision/unassigned`, {
        method: 'POST', headers: hdrs(),
        body: JSON.stringify({ phone_number: phone, forward_to: '+10000000000', channel: 'Unassigned' }),
      });
      if (res.ok) {
        setSearchResults(prev => prev.filter(n => n.phone_number !== phone));
        fetchOwned();
      }
    } catch (e) { console.error(e); }
    setBuying(false);
  };

  const releaseNumber = async (numberId) => {
    if (!window.confirm('Deactivate this tracking number?')) return;
    try {
      await fetch(`${API}/api/admin/call-tracking/release/${numberId}`, { method: 'DELETE', headers: getHeaders() });
      fetchData(); fetchOwned();
    } catch (e) { console.error(e); }
  };

  const unassigned = ownedNumbers.filter(n => !n.assigned);
  const assigned = ownedNumbers.filter(n => n.assigned);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-blue-500 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-call-tracking">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Phone className="w-5 h-5 text-green-500" />
          <h2 className="text-lg font-semibold text-gray-900">Call Tracking Management</h2>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-green-50 text-green-600 text-xs border border-green-200">{ownedNumbers.length} owned</Badge>
          <Badge className="bg-blue-50 text-blue-600 text-xs border border-blue-200">{unassigned.length} available</Badge>
          <Badge className="bg-purple-50 text-purple-600 text-xs border border-purple-200">{assigned.length} assigned</Badge>
        </div>
      </div>

      {/* SEO Guidance */}
      <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="seo-guidance-banner">
        <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-blue-800 leading-relaxed">
          <p className="font-semibold mb-1">SEO-Safe Number Assignment Guide</p>
          <p><strong>GBP:</strong> Tracking number as Primary, real number as Additional</p>
          <p><strong>Website:</strong> Use DNI script (client dashboard) | <strong>Ads/Print:</strong> Safe, not indexed | <strong>Citations:</strong> Always use real number</p>
        </div>
      </div>

      {/* ══════ YOUR NUMBER INVENTORY ══════ */}
      <Card className="bg-white border-2 border-blue-200">
        <CardHeader className="pb-2 bg-blue-50/50">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-sm text-gray-900">Your Number Inventory</CardTitle>
              <p className="text-xs text-gray-500 mt-0.5">Numbers you own in Telnyx — assign them to clients below</p>
            </div>
            <Button size="sm" variant="outline" onClick={fetchOwned} disabled={loadingOwned} className="text-xs border-gray-300">
              {loadingOwned ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Refresh'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-3 space-y-4">
          {loadingOwned ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-blue-400 animate-spin" /></div>
          ) : (
            <>
              {/* Available (Unassigned) Numbers */}
              {unassigned.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-green-700 mb-2">Available ({unassigned.length})</p>
                  <div className="space-y-1">
                    {unassigned.map(n => {
                      const isSelected = selectedNumber === n.phone_number;
                      return (
                        <button key={n.phone_number} onClick={() => setSelectedNumber(n.phone_number)}
                          data-testid={`owned-${n.phone_number}`}
                          className={`w-full flex items-center justify-between rounded-lg px-3 py-2.5 text-left transition-all ${
                            isSelected ? 'bg-blue-50 border-2 border-blue-500 shadow-sm' : 'bg-green-50 border border-green-200 hover:border-blue-300 hover:bg-blue-50'
                          }`}>
                          <div className="flex items-center gap-2">
                            {isSelected && <Check className="w-4 h-4 text-blue-600" />}
                            <PhoneIncoming className={`w-3.5 h-3.5 ${isSelected ? 'text-blue-600' : 'text-green-500'}`} />
                            <span className={`text-sm font-mono ${isSelected ? 'text-blue-700 font-bold' : 'text-gray-900'}`}>{n.phone_number}</span>
                          </div>
                          <Badge className="bg-green-100 text-green-700 text-[9px]">Available</Badge>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Assigned Numbers */}
              {assigned.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-gray-500 mb-2">Assigned ({assigned.length})</p>
                  <div className="space-y-1">
                    {assigned.map(n => (
                      <div key={n.phone_number} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2">
                          <PhoneIncoming className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-xs font-mono text-gray-600">{n.phone_number}</span>
                          <span className="text-xs text-gray-400">→</span>
                          <span className="text-xs text-gray-500">{n.client_slug}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-purple-50 text-purple-600 text-[9px]">
                            <Tag className="w-2.5 h-2.5 mr-0.5 inline" />{n.channel}
                          </Badge>
                          <span className="text-[10px] text-gray-400">→ {n.forward_to}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {ownedNumbers.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4">No numbers found in your Telnyx account</p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ══════ ASSIGN TO CLIENT ══════ */}
      {unassigned.length > 0 && (
        <Card className="bg-white border-gray-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-900">Assign Number to Client</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {selectedNumber ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2">
                <Check className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-mono font-bold text-blue-800">{selectedNumber}</span>
                <button onClick={() => setSelectedNumber('')} className="text-xs text-blue-500 underline ml-2">change</button>
              </div>
            ) : (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3">Select a number from your inventory above first</p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Client</label>
                <select value={assignSlug} onChange={e => handleClientSelect(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-gray-50" data-testid="assign-client-select">
                  <option value="">Select client...</option>
                  {clients.map(c => <option key={c.slug} value={c.slug}>{c.business_name || c.slug}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Forward calls to (real business #)</label>
                <PhoneInput value={assignForward} onChange={setAssignForward} className="bg-gray-50 border-gray-200 text-gray-800" data-testid="assign-forward-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Channel</label>
                <select value={assignChannel} onChange={e => setAssignChannel(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-gray-50" data-testid="assign-channel-select">
                  {CHANNELS.map(ch => <option key={ch} value={ch}>{ch}</option>)}
                </select>
                {CHANNEL_SEO_INFO[assignChannel] && <p className="text-[10px] mt-1 text-gray-400">{CHANNEL_SEO_INFO[assignChannel]}</p>}
              </div>
            </div>

            <Button onClick={assignOwned} disabled={assigning || !selectedNumber || !assignSlug || !assignForward}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm w-full sm:w-auto" data-testid="assign-btn">
              {assigning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Assign {selectedNumber ? selectedNumber : 'Selected Number'} to Client
            </Button>

            {msg && <p className={`text-xs ${msg.includes('Assigned') ? 'text-green-600 font-medium' : 'text-red-500'}`}>{msg}</p>}
          </CardContent>
        </Card>
      )}

      {/* ══════ ACTIVE ASSIGNMENTS (from DB) ══════ */}
      <Card className="bg-white border-gray-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-gray-900">Active Client Assignments</CardTitle>
        </CardHeader>
        <CardContent>
          {overview.clients.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">No tracking numbers assigned to clients yet</p>
          ) : (
            <div className="space-y-4">
              {overview.clients.map(c => (
                <div key={c.client_slug} className="border border-gray-100 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-gray-900">{c.client_slug}</p>
                    <Badge className="bg-blue-50 text-blue-600 text-[10px]">{c.total_calls} total calls</Badge>
                  </div>
                  <div className="space-y-1.5">
                    {c.numbers.map(n => (
                      <div key={n.number_id || n.tracking_number} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-3">
                          <PhoneIncoming className="w-3.5 h-3.5 text-green-500" />
                          <span className="text-xs font-mono text-gray-900">{n.tracking_number}</span>
                          <span className="text-xs text-gray-400">→</span>
                          <span className="text-xs text-gray-600">{n.forward_to}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-purple-50 text-purple-600 text-[9px]">
                            <Tag className="w-2.5 h-2.5 mr-0.5 inline" />{n.channel || 'Unassigned'}
                          </Badge>
                          <span className="text-[10px] text-gray-400">{n.total_calls} calls</span>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                            onClick={() => releaseNumber(n.number_id)} data-testid={`release-${n.number_id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ══════ BUY NEW NUMBERS ══════ */}
      <Card className="bg-white border-gray-200">
        <CardHeader className="pb-2">
          <button onClick={() => setShowBuyNew(!showBuyNew)} className="flex items-center justify-between w-full">
            <CardTitle className="text-sm text-gray-900">Buy New Numbers from Telnyx</CardTitle>
            {showBuyNew ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
          </button>
        </CardHeader>
        {showBuyNew && (
          <CardContent className="space-y-3 pt-2">
            <p className="text-xs text-gray-500">Search Telnyx inventory by area code and purchase new numbers to add to your inventory</p>
            <div className="flex gap-2 items-end">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Area code</label>
                <Input value={areaCode} onChange={e => setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))} placeholder="817" className="w-28 bg-gray-50 border-gray-200 text-gray-800" data-testid="search-area-code" />
              </div>
              <Button onClick={searchNumbers} disabled={searching} className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-9" data-testid="search-numbers-btn">
                {searching ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Search className="w-3 h-3 mr-1" />} Search
              </Button>
            </div>
            {searchResults.length > 0 && (
              <div className="space-y-1 max-h-48 overflow-y-auto border border-gray-100 rounded-lg p-2">
                {searchResults.map(n => (
                  <div key={n.phone_number} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2 border border-gray-100">
                    <div>
                      <span className="text-sm font-mono text-gray-900">{n.phone_number}</span>
                      <span className="text-xs text-gray-500 ml-2">{n.locality} {n.region}</span>
                    </div>
                    <Button size="sm" onClick={() => buyNumber(n.phone_number)} disabled={buying} className="bg-green-600 hover:bg-green-700 text-white text-xs">
                      {buying ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />} Buy
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
}
